import type DemandOption   from "@/domains/DemandsV2/DemandOption";
import type Tender         from "@/domains/Tenders/Tender";
import type TenderBid      from "@/domains/TenderBids/TenderBid";
import type Offer          from "@/domains/Offers/Offer";
import type Qty            from "@/core/types/Qty";
import SerializeSimplified from "@/core/misc/decorators/SerializeSimplified";
import Core                from "@/core/Core";
import OrderCategory       from "@/domains/Orders/OrderCategory";
import DateSimple          from "@/core/types/DateSimple";
import Partner             from "@/domains/Partners/Partner";
import Company             from "@/domains/Companies/Company";
import Person              from "@/domains/Persons/Person";
import Store               from "@/domains/Stores/Store";
import ProductionSite      from "@/domains/ProductionSites/ProductionSite";
import PaymentTerm         from "@/domains/PaymentTerms/PaymentTerm";
import PaymentTermsService from "@/domains/PaymentTerms/PaymentTermsService";
import ContractExternal    from "@/domains/Contracts/ContractExternal";
import ContractPublic      from "@/domains/Contracts/ContractPublic";
import DeliveryType        from "@/domains/DeliveryTypes/DeliveryType";
import Serializable        from "@/core/misc/mixins/Serializable";
import PreorderPosition    from "@/domains/Preorders/PreorderPosition";

// noinspection RedundantConditionalExpressionJS
/**
 * Реквизиты сделки — общий блок полей для Order и OrderPosition.
 *
 * Заказ и позиция держат ССЫЛКУ на один и тот же экземпляр OrderRequisites,
 * пока позиция находится внутри заказа (стадия черновика/оформления).
 * Самостоятельная позиция держит собственный экземпляр.
 *
 * Каскад зависимых полей выражен явно — через цепочку сеттеров,
 * вызывающих друг друга. Граф читается прямо по стеку вызовов:
 *   seller → sellerCompany → sellerStore → responsibleSeller → contract → (поля договора)
 *
 * ВАЖНО: фабрики (fromServer/fromXXX) пишут в приватные поля напрямую,
 * в обход публичных сеттеров — чтобы установка seller не обнулила
 * только что присвоенные зависимые поля. Каскад работает только для UI/селекторов.
 */
class OrderRequisites extends Serializable {

  // ---- Флаги (общие для заказа и позиции) ----
  /** Базируется на предложении */
  public isOfferBased: boolean;
  /** Иностранный продавец */
  public isForeign: boolean;
  /** Является предзаказом */
  public isPreorder: boolean;
  /** Завершён */
  public isFinished: boolean;

  /** Содержит образцы */
  private _isSamples: boolean;
  /** Образцы только для исследований */
  public isForTestsOnly: boolean | null;

  // ---- Категория ----
  @SerializeSimplified
  public orderCategory: OrderCategory;

  // ---- Участники: продавец ----
  @SerializeSimplified
  private _seller: Partner;
  @SerializeSimplified
  private _sellerCompany: Company;
  @SerializeSimplified
  private _sellerStore: Store;

  // ---- Участники: покупатель ----
  @SerializeSimplified
  public buyer: Partner;
  @SerializeSimplified
  public buyerCompany: Company;
  @SerializeSimplified
  public buyerStore: Store;
  @SerializeSimplified
  public buyerProductionSite: ProductionSite | null;

  // ---- Ответственные ----
  @SerializeSimplified
  public responsibleSeller: Person | null;
  @SerializeSimplified
  public responsibleBuyer: Person;

  // ---- Договор ----
  @SerializeSimplified
  private _contract: ContractExternal | ContractPublic | null;

  // ---- Дата заявки ----
  public requestDate: DateSimple | null;

  // ---- Доставка ----
  @SerializeSimplified
  public deliveryType: DeliveryType;
  public deliveryDays: number;
  public isDeliveryPaidByBuyer: boolean;

  private _isVatIncludedSeller: boolean | null;

  // ---- Условия оплаты ----
  public paymentTerms: PaymentTerm[] = [];

  // ---- Тендер ----
  @SerializeSimplified
  public tender: Tender | null;
  @SerializeSimplified
  public tenderBid: TenderBid | null;

  // ==================== Фабрики ====================

  static fromServer(data: any, result = new OrderRequisites()): OrderRequisites {
    // super раскидывает скаляры и сырые объекты по полям (в приватные _поля
    // напрямую, минуя сеттеры — каскад не запускается).
    // Ниже перезатираем сырые объекты гидрированными в их типы.
    super.fromServer(data, result);

    // ---- Категория ----
    result.orderCategory = Core.utils.hydrate(data.orderCategory, OrderCategory.fromServer)!;

    // ---- Участники: продавец (приватные поля, в обход каскада) ----
    result._seller        = Core.utils.hydrate(data.seller, Partner.fromServer)!;
    result._sellerCompany = Core.utils.hydrate(data.sellerCompany, Company.fromServer)!;
    result._sellerStore   = Core.utils.hydrate(data.sellerStore, Store.fromServer)!;

    // ---- Участники: покупатель ----
    result.buyer               = Core.utils.hydrate(data.buyer, Partner.fromServer)!;
    result.buyerCompany        = Core.utils.hydrate(data.buyerCompany, Company.fromServer)!;
    result.buyerStore          = Core.utils.hydrate(data.buyerStore, Store.fromServer)!;
    result.buyerProductionSite = Core.utils.hydrate(data.buyerProductionSite, ProductionSite.fromServer);

    // ---- Ответственные ----
    result.responsibleSeller = Core.utils.hydrate(data.responsibleSeller, Person.fromServer);
    result.responsibleBuyer  = Core.utils.hydrate(data.responsibleBuyer, Person.fromServer)!;

    // ---- Договор (приватное поле, в обход каскада) ----
    if (result.isOfferBased) {
      result._contract = Core.utils.hydrate(data.contract, ContractPublic.fromServer);
    } else {
      result._contract = Core.utils.hydrate(data.contract, ContractExternal.fromServer);
    }

    // ---- Дата заявки ----
    result.requestDate = Core.utils.hydrate(data.requestDate, DateSimple.fromServer)!;

    // ---- Доставка ----
    result.deliveryType = Core.utils.hydrate(data.deliveryType, DeliveryType.fromServer)!;

    // ---- Условия оплаты ----
    result.paymentTerms = Core.utils.hydrateArray(data.paymentTerms, PaymentTerm.fromServer);

    // ---- Тендер ----
    // tender/tenderBid приходят с сервера готовыми, на отправку усекаются @SerializeSimplified
    result.tender    = data.tender ?? null;
    result.tenderBid = data.tenderBid ?? null;

    return result;
  }

  static fromNew(orderCategory: OrderCategory): OrderRequisites {
    const result = new OrderRequisites();

    // ---- Флаги ----
    result.isPreorder = false;
    result.isOfferBased = false;

    // ---- Категория ----
    result.orderCategory = orderCategory;

    if (orderCategory?.id === 'freeSamples') {
      result._isSamples = true;
    }

    if (result._isSamples) {
      result.isForTestsOnly = true;
    }

    // ---- Участники: покупатель (мы сами) ----
    if (Core.currentPartner.isBuyer) {
      result.buyer = Core.currentPartner;
      result.responsibleBuyer = Core.currentUser;
    }

    return result;
  }

  static fromOffer(offer: Offer): OrderRequisites {
    const result = new OrderRequisites();

    // ---- Флаги ----
    result.isOfferBased    = true;
    result.isForeign       = (offer.seller.country?.code && offer.seller.country.code !== Core.config.localCountry.code) ? true : false;
    result.isPreorder      = false;
    result._isSamples      = offer.isSamples;
    result.isForTestsOnly = offer.isSamples ? true : null;

    // ---- Категория ----
    /** Заказы по предложению всегда имеют категорию rawMaterialsAndPackage, пока что, who knows */
    result.orderCategory = OrderCategory.ALL.get('rawMaterialsAndPackage')!;

    // ---- Участники: продавец ----
    result._seller        = offer.seller;
    result._sellerCompany = offer.sellerCompany!;
    result._sellerStore   = offer.sellerStore!;

    // ---- Участники: покупатель ----
    if (Core.currentPartner.isBuyer) {
      result.buyer = Core.currentPartner;
    }
    result.buyerStore = offer.buyerStore!;

    // ---- Ответственные ----
    //result.responsibleSeller = offer.responsibleSeller;

    if (Core.currentPartner.isBuyer) {
      result.responsibleBuyer = Core.currentUser;
    }

    // ---- Договор ----
    //result._contract = undefined;

    // ---- Дата заявки ----
    //result.requestDate = undefined;

    // ---- Доставка ----
    result.deliveryType          = offer.deliveryType;
    result.deliveryDays          = offer.deliveryDays!;
    result.isDeliveryPaidByBuyer = offer.deliveryType.id.startsWith('byBuyer');

    // ---- НДС ----
    result._isVatIncludedSeller = offer.isVatIncludedSeller;

    // ---- Условия оплаты ----
    result.paymentTerms = offer.paymentTerms;

    // ---- Тендер ----
    //result.tender    = undefined;
    //result.tenderBid = undefined;

    return result;
  }

  static fromTenderBid(tenderBid: TenderBid, tender: Tender): OrderRequisites {
    const result = new OrderRequisites();

    // ---- Флаги ----
    /** Я полагаю это верно, потому что всё же в основе предложение */
    result.isOfferBased  = true;
    result.isForeign     = (tenderBid.seller.country?.code && tenderBid.seller.country.code !== Core.config.localCountry.code) ? true : false;
    result.isPreorder    = false;
    //result.isFinished    = undefined;
    result._isSamples    = false;
    //result.isForTestsOnly = null;

    // ---- Категория ----
    /** Заказы по предложению всегда имеют категорию rawMaterialsAndPackage, пока что, who knows */
    result.orderCategory = OrderCategory.ALL.get('rawMaterialsAndPackage')!;

    // ---- Участники: продавец ----
    result._seller        = tenderBid.seller;
    result._sellerCompany = tenderBid.sellerCompany!;
    result._sellerStore   = tenderBid.sellerStore!;

    // ---- Участники: покупатель ----
    result.buyer               = tender.buyer;
    result.buyerCompany        = tender.buyerCompany;
    result.buyerStore          = tender.buyerStore;
    result.buyerProductionSite = tender.buyerProductionSite;

    // ---- Ответственные ----
    result.responsibleSeller = tenderBid.responsibleSeller;
    result.responsibleBuyer  = tender.responsibleBuyer;

    // ---- Договор ----
    //result._contract = undefined;

    // ---- Дата заявки ----
    //result.requestDate = undefined;

    // ---- Доставка ----
    result.deliveryType         = tenderBid.deliveryType;
    result.deliveryDays         = tenderBid.deliveryDays!;
    result.isDeliveryPaidByBuyer = result.deliveryType.id.startsWith('byBuyer');

    // ---- НДС ----
    result._isVatIncludedSeller = tenderBid.isVatIncludedSeller;

    result.paymentTerms = tenderBid.paymentTerms;

    // ---- Тендер ----
    result.tender    = tenderBid.tender;
    result.tenderBid = tenderBid;

    return result;
  }

  static fromDemandV2Option(usedOption: { option: DemandOption, usedQty: Qty }): OrderRequisites {
    const result = new OrderRequisites();

    const demandOption = usedOption.option;
    const demandV2     = demandOption.demand;

    // ---- Флаги ----
    result.isOfferBased  = false;
    result.isForeign     = (demandOption.seller.country?.code && demandOption.seller.country.code !== Core.config.localCountry.code) ? true : false;
    result.isPreorder    = false;
    //result.isFinished    = undefined;
    result._isSamples    = false;
    result.isForTestsOnly = null;

    // ---- Категория ----
    result.orderCategory = OrderCategory.ALL.get('rawMaterialsAndPackage')!;

    // ---- Участники: продавец ----
    result._seller        = demandOption.seller;
    result._sellerCompany = demandOption.sellerCompany!;
    result._sellerStore   = demandOption.sellerStore!;

    // ---- Участники: покупатель ----
    result.buyer               = Core.currentPartner;
    result.buyerCompany        = demandV2.buyerCompany;
    result.buyerStore          = demandV2.buyerStore;
    result.buyerProductionSite = demandV2.buyerProductionSite;

    // ---- Ответственные ----
    //result.responsibleSeller = undefined;
    result.responsibleBuyer  = demandV2.responsibleBuyer;

    // ---- Договор ----
    //result._contract = undefined;

    // ---- Дата заявки ----
    //result.requestDate = undefined;

    // ---- Доставка ----
    result.deliveryType          = demandOption.deliveryType;
    result.deliveryDays          = demandOption.deliveryDays!;
    result.isDeliveryPaidByBuyer = demandOption.deliveryType.id.startsWith('byBuyer');

    // ---- НДС ----
    result._isVatIncludedSeller = demandOption.isVatIncludedSeller;

    // ---- Условия оплаты ----
    /** Захардкожено: постоплата 10 дней с момента получения. Условия
     *  оплаты опции здесь не берутся — так было исторически, менять до
     *  отдельного решения не будем. */
    result.paymentTerms.push(new PaymentTerm(
      PaymentTermsService.getEventById('after_receiving'),
      10,
    ));

    // ---- Тендер ----
    //result.tender    = undefined;
    //result.tenderBid = undefined;

    return result;
  }

  static fromPreorderPosition(preorderPosition: PreorderPosition): OrderRequisites {
    // PreorderPosition — наследник AbstractOrderPosition, у неё уже есть requisites.
    // Берём клон реквизитов предзаказа и переопределяем поля, специфичные для нового заказа.
    const result = Core.utils.cloneDeep(preorderPosition.requisites);

    // ---- Флаги ----
    result.isPreorder = false; // это уже заказ, не предзаказ
    result.isFinished = false;

    // ---- Ответственные ----
    if (Core.currentPartner.isBuyer) {
      result.responsibleBuyer = Core.utils.cloneDeep(Core.currentUser);
    }

    // ---- Дата заявки ----
    result.requestDate = null;

    return result;
  }

  get isSamples(): boolean { return this._isSamples; }
  set isSamples(value: boolean) {
    this._isSamples = value;

    if (this._isSamples) {
      this.isForTestsOnly = true;
    } else {
      this.isForTestsOnly = null;
    }
  }

  get isFreeSamples(): boolean { return this.orderCategory?.id === 'freeSamples' }
  get isPrinting(): boolean { return this.orderCategory?.id === 'printing'; }
  get isRawMaterialsAndPackage(): boolean { return this.orderCategory?.id === 'rawMaterialsAndPackage'; }

  /**
   * Смена продавца обнуляет всю зависимую ветку продавца.
   * buyer-ветку НЕ трогаем — покупатель не зависит от продавца.
   */
  get seller(): Partner { return this._seller; }
  set seller(value: Partner) {
    this._seller = value;

    this.sellerCompany     = undefined as any;
    this.sellerStore       = undefined as any;
    this.responsibleSeller = undefined as any;
  }

  /**
   * Смена юр. лица продавца обнуляет договор.
   * Договор привязан к юр. лицу — при его смене становится невалидным.
   */
  get sellerCompany(): Company { return this._sellerCompany; }
  set sellerCompany(value: Company) {
    this._sellerCompany = value;

    this.contract = undefined as any;
  }

  get sellerStore(): Store { return this._sellerStore; }
  set sellerStore(value: Store) {
    this._sellerStore = value;
  }

  get responsible(): Person | null {
    if (Core.currentPartner?.isSeller) {
      return this.responsibleSeller;
    }
    else {
      return this.responsibleBuyer;
    }
  }

  set responsible(value: Person) {
    if (Core.currentPartner?.isSeller) {
      this.responsibleSeller = value;
    }
    else {
      this.responsibleBuyer = value;
    }
  }

  /**
   * Смена договора обнуляет все его зависимые поля:
   * тип/срок доставки, оплату за счёт покупателя, условия оплаты, режим НДС.
   */
  get contract(): ContractExternal | ContractPublic | null | undefined { return this._contract; }
  set contract(value: ContractExternal | ContractPublic |  null | undefined) {
    this._contract = value as any;

    // deliveryDays договор не задаёт — обнуляем всегда, независимо от наличия контракта.
    this.deliveryDays = undefined as any;

    this.paymentTerms          = [];
    if (this._contract?.prepaymentTerms)  this.paymentTerms.push(...this._contract.prepaymentTerms);
    if (this._contract?.postpaymentTerms) this.paymentTerms.push(...this._contract.postpaymentTerms);

    if (this._contract instanceof ContractPublic) {
      // сюда добавим специфические поля для оферты когда будем переделывать
    } else {
      this.deliveryType          = this._contract?.deliveryType as any;
      this.isDeliveryPaidByBuyer = this._contract?.isDeliveryPaidByBuyer as any;
      this.isVatIncludedSeller   = this._contract?.isVatIncluded as any;
    }
  }

  get isVatPresented(): boolean { return this.sellerCompany && this.sellerCompany.isVatPayer === true }
  get isVatIncludedSeller(): boolean | null { return this._isVatIncludedSeller; }
  set isVatIncludedSeller(value: boolean | null) {
    this._isVatIncludedSeller = value;
  }

  // Удобно обращаться к такому свойству из шаблона, и не писать местячковые решения
  get isMine() {
    if (Core.currentPartner.isBuyer && this.buyer.id === Core.currentPartner.id) return true;
    if (Core.currentPartner.isSeller && this.seller.id === Core.currentPartner.id) return true;
    return false;
  }

  /**
   * Причины, по которым нельзя править реквизиты сделки, общие для заказа
   * и всех его позиций (юр. лицо продавца, продавец, покупатель,
   * производственная площадка, тип доставки, isVatIncludedSeller и т.п.).
   * Их правка на одной позиции рассинхронит суммы остальных позиций и
   * самого заказа — редактирование допустимо только в режиме редактирования
   * заказа.
   *
   * Пустой массив — править можно.
   *
   * Однако у предзаказа и заказа на основе предзаказа нет возможности
   * отредактировать это в заказе, поэтому для них исключение. Исключение
   * решает позиция (PreorderPosition и OrderPosition переопределяют
   * getReasonsCannotChangeDealTerms), здесь — блок для обычных позиций заказа.
   */
  getReasonsCannotChangeDealTerms(): string[] {
    return ['Реквизиты сделки меняются через редактирование заказа'];
  }

  /**
   * Можно ли редактировать критичные поля сделки.
   * Оставлен для вызывающих, которым нужен флаг, а не текст причины.
   */
  get canChangeCriticalFields(): boolean {
    return this.getReasonsCannotChangeDealTerms().length === 0;
  }

  get paymentTermsString(): string {
    if (this.paymentTerms.length === 0) {
      return '--';
    }

    const prepayments: PaymentTerm[] = [];
    const postpayments: PaymentTerm[] = [];

    for (const term of this.paymentTerms) {
      if (term.event?.isPrepayment) {
        prepayments.push(term);
      } else {
        postpayments.push(term);
      }
    }

    const parts: string[] = [];

    if (prepayments.length > 0) {
      const body = prepayments
        .map(term => term.toString())
        .join(', ');
      parts.push(`предоплата ${body}`);
    }

    if (postpayments.length > 0) {
      const body = postpayments
        .map(term => term.toString())
        .join(', ');
      parts.push(`постоплата ${body}`);
    }

    return parts.join(', ');
  }

  /** Строка доставки заказа */
  get deliveryString(): string {
    const { deliveryType, deliveryDays, isDeliveryPaidByBuyer } = this;

    let result = '';

    /** На тот случай, если тип доставки будет известен до кол-ва дней в пути
     * а такое может быть при ранней инициализации контракта,
     * а такое происходит в случае ранней инициализации sellerCompany и sellerBuyer,
     * например через responsibleFilter */
    if (deliveryType?.name) {
      result += deliveryType.name.toLowerCase();
    }

    if (deliveryDays) {
      result += ` за ${deliveryDays} ${Core.utils.regional.ru.pluralize(deliveryDays, 'день', 'дня', 'дней')}`;
    }

    if (typeof isDeliveryPaidByBuyer === 'boolean') {
      result += isDeliveryPaidByBuyer ? ' (за счёт покупателя)' : ' (за счёт продавца)';
    }

    return result;
  }

  /** НДС в цене продавца */
  get vatIncludedSellerString(): string {
    if (this.isVatIncludedSeller === true) {
      return 'в т.ч';
    } else if (this.isVatIncludedSeller === false) {
      return 'сверху'
    }
    else {
      return 'не облагается';
    }
  }
}

export default OrderRequisites;
